import { useEffect, useState } from 'react'
import { previewSchedule } from '../../agreements/api'
import type { AgreementInput, SchedulePreview } from '../../agreements/types'
import type { FollowUp } from '../types'

const DELAY_MS = 300

/** The follow-up of a proposal in the words of the schedule preview endpoint. */
export function followUpInput(follow: FollowUp | null | undefined): Partial<AgreementInput> | null {
  if (!follow?.schedule) return null
  return {
    schedule: follow.schedule,
    interval_count: follow.interval_count ?? null,
    interval_unit: follow.interval_unit ?? null,
    anchor_on: follow.first_due_on ?? null,
  }
}

/**
 * The live preview of the deadlines (`POST /crm/agreements/schedule-preview`, E4): the same
 * arithmetic as the agreement dialog. `input` null means there is nothing to preview.
 */
export function useSchedulePreview(
  input: (Partial<AgreementInput> & { agreement_id?: string }) | null,
): { preview: SchedulePreview | null; problem: string | null } {
  const [state, setState] = useState<{ key: string; preview: SchedulePreview | null; problem: string | null } | null>(null)
  const key = input ? JSON.stringify(input) : null
  useEffect(() => {
    if (!input || !key) return
    const controller = new AbortController()
    const timer = window.setTimeout(() => {
      previewSchedule({ title: 'Voorbeeld', ...input }, controller.signal)
        .then((preview) => !controller.signal.aborted && setState({ key, preview, problem: null }))
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return
          setState({ key, preview: null, problem: caught instanceof Error ? caught.message : 'Voorbeeld laden mislukt.' })
        })
    }, DELAY_MS)
    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
    // `key` stands for `input`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])
  const current = state && state.key === key ? state : null
  return { preview: current?.preview ?? null, problem: current?.problem ?? null }
}
